import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { AppKitProvider, useAppKit } from '@reown/appkit/react';
import { web3Modal } from '../wallet/web3modal';
import { BrowserProvider, Eip1193Provider, JsonRpcSigner, ethers, BigNumberish } from 'ethers';
import { PLATFORM_FEE_PERCENT } from '@/config';

// --- Types and Interfaces ---
export interface Balances {
  eth: number;
  kala: number;
  usd: number;
  eur: number;
  inr: number;
  matic: number;
}

interface NativeCurrency {
    name: string;
    symbol: string;
    decimals: number;
}

interface NetworkConfig {
    chainId: string;
    chainName: string;
    nativeCurrency: NativeCurrency;
    rpcUrls: string[];
    blockExplorerUrls: string[];
}

interface SwitchChainError extends Error {
    code: number;
}

const TOKEN_CONTRACT_ADDRESSES = {
  sepolia: {
    kala: '0x4934a84351a8e3ac83190841f89a7209559952b1',
  },
  amoy: {
    kala: '0xe9E5D4a9B223679eB86A0a913A8FafD73C61',
  },
  mainnet: {
    kala: '0xYourKalaTokenAddressOnMainnet', // Placeholder
  }
};

const networks: { [key: string]: NetworkConfig } = {
  '80002': { // Amoy Testnet
    chainId: '0x13882',
    chainName: 'Amoy',
    nativeCurrency: {
        name: 'MATIC',
        symbol: 'MATIC',
        decimals: 18,
    },
    rpcUrls: ['https://rpc-amoy.polygon.technology/'],
    blockExplorerUrls: ['https://www.oklink.com/amoy'],
  },
  '137': { // Polygon Mainnet
    chainId: '0x89',
    chainName: 'Polygon Mainnet',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MATIC',
      decimals: 18,
    },
    rpcUrls: ['https://polygon-rpc.com/'],
    blockExplorerUrls: ['https://polygonscan.com/'],
  }
};

interface WalletContextType {
  isConnected: boolean;
  isConnecting: boolean;
  address?: string;
  provider?: BrowserProvider;
  signer?: JsonRpcSigner;
  open: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  getChainId: () => number | undefined;
  signMessage: (message: string) => Promise<string | undefined>;
  balances: Balances;
  swap: (from: keyof Balances, to: keyof Balances, amount: number) => Promise<void>;
  switchNetwork: (chainId: string) => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
};

function timeout<T>(ms: number, promise: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Promise timed out')), ms);
    promise
      .then(value => { clearTimeout(timer); resolve(value); })
      .catch(reason => { clearTimeout(timer); reject(reason); });
  });
}

export const WalletProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <AppKitProvider {...web3Modal}>
      <WalletLogic>{children}</WalletLogic>
    </AppKitProvider>
  );
};

const initialBalances: Balances = {
    eth: 0,
    kala: 500,
    usd: 1000,
    eur: 1000,
    inr: 80000,
    matic: 10
};

const platformWallet = { kala: 0, matic: 0 };

const WalletLogic: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { state, disconnect, open } = useAppKit();
    const { address, networkId, isConnected } = state || {};
    const walletProvider = state?.provider;
    const [isConnecting, setIsConnecting] = useState(false);
    const [provider, setProvider] = useState<BrowserProvider | undefined>();
    const [signer, setSigner] = useState<JsonRpcSigner | undefined>();
    const [balances, setBalances] = useState<Balances>(initialBalances);

    const openModal = useCallback(async () => {
        if(open) {
            setIsConnecting(true);
            try {
                await open();
            } catch (err: unknown) {
                console.error('Modal open error:', err);
            } finally {
                setIsConnecting(false);
            }
        }
    }, [open]);

    const disconnectWallet = useCallback(async () => {
        if(disconnect) {
            try { await disconnect(); } catch (err) { console.error('Modal disconnect error:', err); }
        }
    }, [disconnect]);

    useEffect(() => {
      if (isConnected && walletProvider) {
        const ethersProvider = new BrowserProvider(walletProvider as Eip1193Provider);
        setProvider(ethersProvider);
        ethersProvider.getSigner().then(setSigner).catch(() => setSigner(undefined));
      } else {
        setProvider(undefined);
        setSigner(undefined);
        setBalances(initialBalances);
      }
    }, [isConnected, walletProvider]);

    useEffect(() => {
        const updateBalances = async () => {
            if (!provider || !address || !networkId) {
                setBalances(initialBalances);
                return;
            }

            let ethBalance = 0;
            let kalaBalance = 0;
            let maticBalance = 0;

            try {
                const balance = await provider.getBalance(address);
                if (networkId === 80002 || networkId === 137) {
                    maticBalance = parseFloat(ethers.formatEther(balance));
                } else {
                    ethBalance = parseFloat(ethers.formatEther(balance));
                }
            } catch (error) {
                console.error("Failed to fetch native on-chain balance:", error);
            }

            const networkName = networkId === 11155111 ? 'sepolia' : networkId === 80002 ? 'amoy' : null;
            if (networkName) {
                try {
                    const kalaContractAddress = TOKEN_CONTRACT_ADDRESSES[networkName].kala;
                    const abi = ["function balanceOf(address) view returns (uint256)"];
                    const contract = new ethers.Contract(kalaContractAddress, abi, provider);

                    const balance: BigNumberish = await timeout(5000, contract.balanceOf(address));
                    kalaBalance = parseFloat(ethers.formatUnits(balance, 18));
                } catch (error) {
                    console.error(`Failed to fetch KALA balance on chain ${networkId}:`, error);
                }
            }
            setBalances(prev => ({ ...prev, eth: ethBalance, kala: kalaBalance, matic: maticBalance }));
        };

        updateBalances();
        const interval = setInterval(updateBalances, 60000);
        return () => clearInterval(interval);

    }, [provider, address, networkId]);

    const signMessage = useCallback(async (message: string): Promise<string | undefined> => {
        if (!provider) return undefined;
        const signer = await provider.getSigner();
        return await signer.signMessage(message);
    }, [provider]);

    const swap = useCallback(async (from: keyof Balances, to: keyof Balances, amount: number) => {
        const RATES: Record<string, number> = {
            eth: 2200, kala: 0.15, usd: 1, eur: 1.09, inr: 0.012, matic: 0.55,
        };

        console.log(`Processing mock swap of ${amount} ${from} to ${to}`);

        return new Promise<void>((resolve, reject) => {
            setTimeout(() => {
                setBalances(prev => {
                    const newBalances = { ...prev };
                    const fee = (amount * PLATFORM_FEE_PERCENT) / 100;
                    const totalDeducted = amount + fee;

                    if (newBalances[from] < totalDeducted) {
                        console.error('Mock swap failed: Insufficient balance.');
                        reject(new Error('Insufficient balance.'));
                        return prev;
                    }

                    const fromRate = RATES[from] || 1;
                    const toRate = RATES[to] || 1;
                    const swapRate = fromRate / toRate;

                    newBalances[from] -= totalDeducted;
                    newBalances[to] = (newBalances[to] || 0) + amount * swapRate;

                    if (from === 'kala' || from === 'matic') {
                        const platformFeeKey = from as keyof typeof platformWallet;
                        platformWallet[platformFeeKey] = (platformWallet[platformFeeKey] || 0) + fee;
                        console.log(`Platform wallet ${from.toUpperCase()} balance: ${platformWallet[platformFeeKey]}`)
                    }

                    console.log('Mock swap successful!');
                    return newBalances;
                });
                resolve();
            }, 1500);
        });
    }, []);

    const switchNetwork = useCallback(async (chainId: string) => {
        if (!walletProvider) {
          console.error("Wallet provider is not available to switch network.");
          return;
        }
        const hexChainId = `0x${parseInt(chainId, 10).toString(16)}`;
        try {
          await (walletProvider as Eip1193Provider).request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: hexChainId }],
          });
        } catch (error: unknown) {
          const switchError = error as SwitchChainError;
          if (switchError.code === 4902) {
            if (!networks[chainId]) {
              console.error(`Configuration for network ${chainId} not found.`);
              return;
            }
            try {
              await (walletProvider as Eip1193Provider).request({
                method: 'wallet_addEthereumChain',
                params: [networks[chainId]],
              });
            } catch (addError) {
              console.error(`Failed to add network ${chainId}:`, addError);
            }
          } else {
              console.error(`Failed to switch to network ${chainId}:`, switchError);
          }
        }
      }, [walletProvider]);

    const value: WalletContextType = {
        isConnected: isConnected || false,
        isConnecting,
        address,
        provider,
        signer,
        open: openModal,
        connect: openModal,
        disconnect: disconnectWallet,
        getChainId: () => networkId,
        signMessage,
        balances,
        swap,
        switchNetwork,
    };

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
};